import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4112">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4113">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4114">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4115">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4116" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4117">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4118" />
              <p className="text-sm text-gray-600" data-id="element-4119">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4120">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4121" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4122">
          {/* Quality & Metadata */}
          <div data-id="element-4123">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4124">
              <Brain className="w-3.5 h-3.5" data-id="element-4125" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4126">
              <div className="grid grid-cols-2 gap-4" data-id="element-4127">
                <div data-id="element-4128">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4129">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4130">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4131">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4132">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4133">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4134">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4135">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4136">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4137">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4138">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4139">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4140">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4141">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4142">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4143">
              <Database className="w-3.5 h-3.5" data-id="element-4144" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4145">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4146" />
                <span className="text-sm text-gray-600" data-id="element-4147">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4148">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4149">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4150">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4151">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4152" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4153">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4154">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4155" />
                  <span className="text-sm" data-id="element-4156">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4157">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4158" />
                  <span className="text-sm" data-id="element-4159">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4160">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4161">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4162" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4163">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4164">
                      <div data-id="element-4165">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4166">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4167">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4168">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4169">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4170">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4171">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4172">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_MISCONCEPTION_MISSING') ? <span className="text-amber-500" data-id="element-4173">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4174">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4175">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4176" />
                        <span className="text-sm text-gray-600" data-id="element-4177">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4178">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4179" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4180" />}
                        <span className="text-sm text-gray-600" data-id="element-4181">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}